/**
 * Zentraler defensiver Wrapper um die iframe-resizer parentIFrame-API (v4.3.2).
 *
 * Die App läuft als iFrame in Kundenwebsites eingebettet. Die parentIFrame-API
 * existiert nur, wenn der Eltern-Embed sie freigibt (iFrameResize mit
 * enablePublicMethods:true). Alte Embeds geben sie NICHT frei — jeder Aufruf
 * muss daher defensiv sein (No-Op, wenn parentIFrame fehlt) und darf den
 * Buchungs-/Navigations-Flow niemals crashen.
 *
 * Bewusst KEIN `declare global { interface Window { parentIFrame } }`: eine
 * globale Window-Augmentation würde mit jeder anderen Deklaration desselben
 * Feldes kollidieren (TS2717). Stattdessen ein eng typisierter Cast über einen
 * lokalen Accessor — alle Aufrufer importieren die Helfer hier.
 */

/**
 * Von iframe-resizer per getPageInfo gelieferte Layout-Information des
 * Eltern-Fensters relativ zum eingebetteten iFrame. Alle Werte in Pixeln.
 */
export type ParentPageInfo = {
    iframeHeight: number
    iframeWidth: number
    offsetTop: number
    offsetLeft: number
    scrollTop: number
    scrollLeft: number
    documentHeight: number
    documentWidth: number
    windowHeight: number
    windowWidth: number
    clientHeight: number
    clientWidth: number
}

type ParentIFrameApi = {
    scrollToOffset(x: number, y: number): void
    moveToAnchor(anchor: string): void
    // getPageInfo(false) beendet ein laufendes Abo (iframe-resizer-API)
    getPageInfo(callback: ((pageInfo: ParentPageInfo) => void) | false): void
    autoResize(enable: boolean): boolean
    size(customHeight?: number, customWidth?: number): void
    sendMessage(message: unknown, targetOrigin?: string): void
}

/**
 * Liefert die parentIFrame-API, falls der Eltern-Embed sie freigegeben hat,
 * sonst undefined. Eng typisierter Cast statt globaler Window-Augmentation.
 */
function getParentIFrame(): ParentIFrameApi | undefined {
    if (typeof window === "undefined") return undefined
    return (window as unknown as { parentIFrame?: ParentIFrameApi }).parentIFrame
}

/**
 * Host-Brücke des Embed-Modus (Web-Modul ohne iFrame, sc-3381): dort gibt es keinen iframe-resizer,
 * der Embed-Einstieg registriert stattdessen eine eigene Scroll-Funktion. Bewusst getrennt von
 * `hasParentIFrame()` (iframeOverlay und getPageInfo bleiben iFrame-only).
 * Die Embed-Implementierung scrollt immer zum Modulanfang; alle heutigen Aufrufer von
 * parentScrollToOffset übergeben (0, 0). Ein Versatz wäre im Embed wirkungslos.
 */
export type HostBridge = {
    scrollToOffset(x: number, y: number): void
}
let hostBridge: HostBridge | undefined

export function registerHostBridge(bridge: HostBridge | undefined): void {
    hostBridge = bridge
}

/**
 * True, wenn der Eltern-Embed die parentIFrame-API freigegeben hat.
 */
export function hasParentIFrame(): boolean {
    return Boolean(getParentIFrame())
}

/**
 * Handshake-Gate für sendMessage-basierte Parent-Kommunikation.
 *
 * Nur UNSER Parent-Wrapper (public/js/iframeResizer.min.js) kündigt sich nach
 * dem iFrame-Init per Handshake-Message `koParentReady` an. Erst danach dürfen
 * wir per sendMessage koScrollToCourse senden. Grund: Alte/fremde Embeds haben
 * teils noch Demo-Code (`alert(messageData.message)`) im messageCallback/onMessage
 * — ohne Handshake würde unsere erste sendMessage dort einen nativen Alert
 * `[object Object]` auf der Kundenseite auslösen. Kein Handshake -> stilles No-Op.
 *
 * Die Handshake-Message empfängt der inline-Handler in public/index.html (läuft
 * als reines JS vor dem Bundle) und erreicht dieses Modul nur über window: Er
 * setzt `window.__koParentReady` und ruft — falls das Bundle schon geladen ist —
 * `window.__koSetParentReady()`. Wir spiegeln beide Richtungen, um die Race
 * zwischen Bundle-Load und Handshake-Zeitpunkt abzudecken.
 */
let parentReady = false

/**
 * Setzt das Handshake-Gate frei. Wird vom inline-onMessage-Empfang in
 * public/index.html aufgerufen (über die window-Brücke), sobald der
 * Parent-Wrapper `koParentReady` gesendet hat.
 */
export function setParentReady(): void {
    parentReady = true
}

/**
 * Zweites Gate nur für Conversion-Meldungen (sc-3366).
 *
 * `koParentReady` senden alle Wrapper seit Juli 2026, auch solche ohne
 * Conversion-Zweig. Ein solcher Wrapper würde `koConversion` an den Kunden-Handler
 * durchreichen (Alt-Embeds: `alert(messageData.message)`), etwa auf einer
 * Kundenseite, die beim Deploy offen war und den alten Wrapper noch im Speicher
 * hat. Deshalb kündigt nur ein Wrapper, der Conversions kennt, zusätzlich
 * `{ koParentConversionReady: token }` an. Das Token geht mit jeder Conversion
 * zurück; der Wrapper nimmt nur Meldungen mit dem Token seines App-iFrames an.
 * Brücke wie bei `koParentReady`.
 */
let parentConversionToken: string | undefined

/**
 * Setzt das Conversion-Gate frei. Wird vom inline-onMessage-Empfang in
 * public/index.html aufgerufen, sobald der Wrapper das Token sendet.
 */
export function setParentConversionReady(token: string): void {
    parentConversionToken = token
}

type KoWindowBridge = {
    __koParentReady?: boolean
    __koSetParentReady?: () => void
    __koParentConversionToken?: string
    __koSetParentConversionReady?: (token: string) => void
}

if (typeof window !== "undefined") {
    const bridge = window as unknown as KoWindowBridge
    if (bridge.__koParentReady) parentReady = true // Handshake kam vor Bundle-Load
    bridge.__koSetParentReady = setParentReady // Handshake kommt nach Bundle-Load
    if (bridge.__koParentConversionToken) parentConversionToken = bridge.__koParentConversionToken
    bridge.__koSetParentConversionReady = setParentConversionReady
}

/**
 * Scrollt das Eltern-Fenster an den gegebenen Offset (relativ zum iFrame).
 * No-Op ohne parentIFrame; Fehler werden geschluckt (nur console.warn).
 */
export function parentScrollToOffset(x: number, y: number): void {
    try {
        const api = getParentIFrame()
        if (api) api.scrollToOffset(x, y)
        else hostBridge?.scrollToOffset(x, y)
    } catch (error) {
        console.warn("[parentIFrame] scrollToOffset fehlgeschlagen:", error)
    }
}

/**
 * Scrollt das Eltern-Fenster zu einem benannten Anker innerhalb des iFrames
 * (Element-id). No-Op ohne parentIFrame; Fehler werden geschluckt.
 */
export function parentMoveToAnchor(anchor: string): void {
    try {
        getParentIFrame()?.moveToAnchor(anchor)
    } catch (error) {
        console.warn("[parentIFrame] moveToAnchor fehlgeschlagen:", error)
    }
}

/**
 * Bittet den zentralen Eltern-Wrapper (public/js/iframeResizer.min.js), einen
 * Kurs ins Bild zu scrollen. `top`/`height` sind Position und Höhe des
 * Kurs-Elements relativ zum iFrame-Dokumentanfang (im iFrame gemessen). Die
 * eigentliche Scroll-Berechnung passiert bewusst im Eltern-Kontext, weil nur
 * dort die Höhe eines fixed/sticky Kundenheaders bekannt ist: der Kurs wird
 * sichtbar gemacht, die iFrame-Oberkante (Listenanfang) bleibt sichtbar wenn
 * möglich, sonst wird der Kurs mittig im sichtbaren Bereich platziert.
 * No-Op ohne parentIFrame (alte Embeds ohne enablePublicMethods).
 *
 * Zusätzlich durch das Handshake-Gate abgesichert: sendet nur, wenn unser
 * Parent-Wrapper sich per `koParentReady` angekündigt hat (siehe parentReady).
 * Alte/fremde Embeds ohne Handshake -> stilles No-Op, damit unsere Message
 * nicht in einem alert-Demo-Handler der Kundenseite landet.
 */
export function parentScrollCourseIntoView(top: number, height: number): void {
    if (!parentReady) return
    try {
        getParentIFrame()?.sendMessage({ koScrollToCourse: { top, height } })
    } catch (error) {
        console.warn("[parentIFrame] scrollCourseIntoView fehlgeschlagen:", error)
    }
}

/**
 * Conversion für den dataLayer der Kundenseite (sc-3366). Bewusst ohne Kurs-,
 * Standort- oder Personendaten: Meta verbietet Daten von Kindern unter 13 und
 * Gesundheitsdaten.
 */
export type ParentConversion =
    | { event: "booking_completed"; transactionId: string; value?: number; currency?: string }
    | { event: "interest_registered"; transactionId: string }

/**
 * Meldet eine Conversion an den Parent-Wrapper, der sie in `window.dataLayer`
 * der Kundenseite schreibt (Ereignisse `ko_booking_completed` und
 * `ko_interest_registered`). Stilles No-Op ohne Token aus `koParentConversionReady`:
 * alter oder fremder Wrapper, Aufruf ohne Elternseite. Liefert `true`, wenn die
 * Nachricht abgesetzt wurde (Abdeckungszähler, `reportEmbedConversion`).
 */
let conversionSink: ((conversion: ParentConversion) => void) | undefined

/** Embed ohne iFrame (src/embed): Conversions gehen statt an den Wrapper an diesen Sink (schreibt selbst in den dataLayer) */
export function setConversionSink(sink: (conversion: ParentConversion) => void): void {
    conversionSink = sink
}

export function parentReportConversion(conversion: ParentConversion): boolean {
    if (conversionSink) {
        conversionSink(conversion)
        return true
    }
    if (!parentConversionToken) return false
    const api = getParentIFrame()
    if (!api) return false
    try {
        api.sendMessage({ koConversion: { ...conversion, token: parentConversionToken } })
        return true
    } catch (error) {
        console.warn("[parentIFrame] reportConversion fehlgeschlagen:", error)
        return false
    }
}

/**
 * Wert und Währung einer Buchung für die Conversion: Summe aller Raten. Sie
 * entspricht Booking.grossPrice, also brutto nach Rabatt und Guthaben. Bei 0 €
 * oder unbekannter Währung fehlen beide Felder (ein Wert 0 verzerrt die
 * Wertoptimierung bei Meta).
 */
export function bookingConversionValue(
    scheduledPayments: Array<{ paymentAmount?: number | null } | null> | null | undefined,
    currency: string | null | undefined
): { value?: number; currency?: string } {
    const sum = (scheduledPayments ?? []).reduce((total, sp) => total + (typeof sp?.paymentAmount === "number" ? sp.paymentAmount : 0), 0)
    const value = Math.round(sum * 100) / 100
    if (!currency || !(value > 0)) return {}
    return { value, currency: currency.toUpperCase() }
}

/**
 * Abonniert die Layout-Information des Eltern-Fensters (kommt initial und
 * danach bei jedem Scroll/Resize des Parents per postMessage — also immer
 * asynchron). Liefert eine Unsubscribe-Funktion. Ohne parentIFrame (alte
 * Embeds, Standalone) wird der Callback nie aufgerufen und die Unsubscribe-
 * Funktion ist ein No-Op.
 */
export function parentSubscribePageInfo(callback: (pageInfo: ParentPageInfo) => void): () => void {
    const api = getParentIFrame()
    if (!api) return () => undefined
    try {
        api.getPageInfo(callback)
    } catch (error) {
        console.warn("[parentIFrame] getPageInfo fehlgeschlagen:", error)
        return () => undefined
    }
    return () => {
        try {
            api.getPageInfo(false)
        } catch (error) {
            console.warn("[parentIFrame] getPageInfo(false) fehlgeschlagen:", error)
        }
    }
}

/**
 * Gibt die automatische Höhenmessung des iFrames frei (true) oder friert die
 * zuletzt gemeldete Höhe ein (false). No-Op ohne parentIFrame.
 */
export function parentAutoResize(enable: boolean): void {
    try {
        getParentIFrame()?.autoResize(enable)
    } catch (error) {
        console.warn("[parentIFrame] autoResize fehlgeschlagen:", error)
    }
}
